<template>
<div class="games">
    <div class="padded">
        <div class="grid-nav">
            <v-select
                v-model="sortFilter"
                class="filter"
                :clearable="false"
                :searchable="false"
                :options="sortOptions"
            >
                <template v-slot:selected-option="{ label }">
                    <div class="selected">
                        {{ $t('GAMES.SORT_BY', {label: label}) }}
                    </div>
                </template>
                <template v-slot:open-indicator="{ attributes }">
                    <span v-bind="attributes">
                        <NuxtImg alt="" src="/images/icons/platforms/down-arrow.png" />
                    </span>
                </template>
            </v-select>
            <v-select
                v-model="packFilter"
                class="filter pack-filter"
                :clearable="false"
                :searchable="false"
                :options="packOptions"
            >
                <template v-slot:selected-option="{ label }">
                    <div class="selected">
                        {{ `Pack: ${label}` }}
                    </div>
                </template>
                <template v-slot:open-indicator="{ attributes }">
                    <span v-bind="attributes">
                        <NuxtImg alt="" src="/images/icons/platforms/down-arrow.png" />
                    </span>
                </template>
            </v-select>
            <v-select
                v-model="playerFilter"
                class="filter player-filter"
                :clearable="false"
                :searchable="false"
                :options="playerOptions"
            >
                <template v-slot:selected-option="{ label }">
                    <div class="selected">
                        {{ $t('GAMES.PLAYER_COUNT', {label: label}) }}
                    </div>
                </template>
                <template v-slot:open-indicator="{ attributes }">
                    <span v-bind="attributes">
                        <NuxtImg alt="" src="/images/icons/platforms/down-arrow.png" />
                    </span>
                </template>
            </v-select>
            <span>
                <i class="fa-solid fa-magnifying-glass search-icon" />
            </span>
            <input v-model="searchText" :placeholder="$t('GAMES.SEARCH_PLACEHOLDER')" class="filter search">
            <ButtonElement class="outline clear-view" :icon="'xmark'" :ext="'png'" v-on:click="onClear">
                {{ $t('GAMES.CLEAR_FILTERS') }}
            </ButtonElement>
        </div>
    </div>

    <h1 v-if="i18n.locale.value !== 'en'" class="locale-heading">
        {{ $t('GAMES.FILTERED_LANG', { selectedLanguage: currentLanguage }) }}
    </h1>
    <div class="padded">
        <h2 v-if="isLoading">{{ $t('PACKS.LOADING') }}</h2>
        <div v-if="sortedGames" class="game-grid">
            <GameTile
                v-for="(game, index) in sortedGames.value"
                :key="`${game.title}-${index}`"
                :game="game"
                class="game-tile"
            />
        </div>
        <NotFound
            v-else-if="!isLoading"
            :text="'GAMES.NO_RESULTS'"
        />
    </div>

    <h1 v-if="i18n.locale.value !== 'en'" class="locale-heading">
        {{ $t('GAMES.IN_ENGLISH', { selectedLanguage: currentLanguage }) }}
    </h1>
    <div class="padded">
        <h2 v-if="isLoading && i18n.locale.value !== 'en'">{{ $t('PACKS.LOADING') }}</h2>
        <div v-if="sortedEnglishGames" class="game-grid">
            <GameTile
                v-for="(game, index) in sortedEnglishGames"
                :key="`${game.title}-${index}`"
                :game="game"
                class="game-tile"
            />
        </div>
        <NotFound
            v-else-if="!isLoading && !sortedEnglishGames && i18n.locale.value !== 'en'"
            :text="'GAMES.NO_RESULTS'"
        />
    </div>
</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import GameTile from '$components/GameTile.vue'
import NotFound from '$components/NotFound.vue'
import ButtonElement from '$components/ButtonElement.vue'
import { JBGWeb } from '$types/JBGWeb'
import { currentCountry } from '$services/shopify'

const i18n = useI18n()
const app = useNuxtApp()

type FilterOption = {
    label: string
}

interface PlayerOption extends FilterOption {
    value: number
}

interface PackOption extends FilterOption {
    value: string
}
interface SortOption extends FilterOption {
    value: string
}

interface AllGames {
    games: JBGWeb.GameDetail[]
    englishGames: JBGWeb.GameDetail[]
}

const isLoading = ref(false)
const packFilter: Ref<PackOption> = ref({ label: i18n.t('GAMES.ANY'), value: i18n.t('GAMES.ANY') })
const playerFilter: Ref<PlayerOption> = ref({ label: i18n.t('GAMES.ANY'), value: -1 })
const searchText = ref('')
const sortOptions: SortOption[] = <SortOption[]>[
    {
        label: i18n.t('GAMES.RELEASE_DATE'),
        value: 'release-date'
    },
    {
        label: i18n.t('GAMES.ALPHABETICAL'),
        value: 'alphabetical'
    }
]
const sortFilter: Ref<SortOption> = ref(sortOptions[0])

useHead({
    title: `${i18n.t('NAVBAR.GAMES')}`,
    link: [
        {
            rel: 'canonical',
            href: 'https://www.jackboxgames.com/games'
        },
        {
            rel: 'alternate',
            hreflang: 'x-default',
            href: 'https://www.jackboxgames.com/games'
        },
        {
            rel: 'alternate',
            hreflang: 'en',
            href: 'https://www.jackboxgames.com/games'
        },
        {
            rel: 'alternate',
            hreflang: 'fr',
            href: 'https://www.jackboxgames.com/fr/games'
        },
        {
            rel: 'alternate',
            hreflang: 'it',
            href: 'https://www.jackboxgames.com/it/games'
        },
        {
            rel: 'alternate',
            hreflang: 'de',
            href: 'https://www.jackboxgames.com/de/games'
        },
        {
            rel: 'alternate',
            hreflang: 'es-ES',
            href: 'https://www.jackboxgames.com/es/games'
        },
        {
            rel: 'alternate',
            hreflang: 'es',
            href: 'https://www.jackboxgames.com/es/games'
        },
        {
            rel: 'alternate',
            hreflang: 'es-MX',
            href: 'https://www.jackboxgames.com/es-XL/games'
        },
        {
            rel: 'alternate',
            hreflang: 'pt-BR',
            href: 'https://www.jackboxgames.com/pt-BR/games'
        }
    ],
    meta: [
        { name: 'description', content: `${i18n.t('META.GAMES')}` }
    ]
})

const { data, refresh: refreshGamesData } = await useAsyncData<AllGames>(
    `games-${i18n.locale.value}`,
    async () => {
        isLoading.value = true
        // Fetch all games in English
        const allGames = (await app.$cms.fetchMany<JBGWeb.GameDetail[]>('game-details', {
            populate: ['tile', 'hero', 'packDetails', 'features.counts', 'features.languages', 'hidden'],
            locale: 'en',
            countryCode: currentCountry.code
        })).filter((game) => !game.hidden)

        const games: JBGWeb.GameDetail[] = []
        const englishGames: JBGWeb.GameDetail[] = []

        // if the language is not english, filter out games with supported language
        if (i18n.locale.value !== 'en') {
            for (let i = 0; i < allGames.length; i++) {
                const game = allGames[i]

                // Get current language in the format that Strapi expects
                const currentLanguage = cmsLanguageFormat(i18n.locale.value)
                let hasLanguageSupport = false

                // Iterate over Strapi's `features.languages` field to determine language support
                for (let j = 0; j < game.features.languages.length; j++) {
                    const supportedLanguage = game.features.languages[j].language
                    if (supportedLanguage === currentLanguage) {
                        hasLanguageSupport = true
                        games.push(game)
                    }
                }
                if (!hasLanguageSupport) {
                    englishGames.push(game)
                }
            }
        } else {
            games.push(...allGames)
        }

        isLoading.value = false
        return { games, englishGames }
    }
)

const currentLanguage = computed(() => {
    switch (i18n.locale.value) {
    case 'fr':
        return 'Français'
    case 'it':
        return 'Italiano'
    case 'de':
        return 'Deutsch'
    case 'es':
        return 'Castellano'
    case 'es-XL':
    case 'es-419':
        return 'Español (América Latina)'
    case 'pt-br':
    case 'pt-BR':
        return 'Português brasileiro'
    default:
        return 'English'
    }
})

function cmsLanguageFormat(lang: string) {
    switch (lang) {
    case 'en':
        return 'en_US'
    case 'fr':
        return 'fr_FR'
    case 'de':
        return 'de_DE'
    case 'it':
        return 'it_IT'
    case 'es':
        return 'es_ES'
    case 'es-XL':
        return 'es_XL'
    case 'pt-BR':
        return 'pt_BR'
    default:
        return 'en_US'
    }
}

watch(i18n.locale, async () => {
    await refreshGamesData()
})

const filteredGames = computed(() => data.value.games.filter((game) => {
    if (searchText.value) {
        if (!game.title.toLowerCase().includes(searchText.value.toLowerCase())) {
            return false
        }
    }

    // a safe guard in case playerMin or playMax is missing
    if (!game.features.counts) return false

    if (
        playerFilter?.value.value !== -1 && game.features.counts
                    && (
                        playerFilter?.value.value < game.features.counts.playerMin
                        || playerFilter?.value.value > game.features.counts.playerMax
                    )
    ) {
        return false
    }

    if (packFilter.value.value === i18n.t('GAMES.ANY')) {
        return true
    }

    if (!((game.packDetails?.data?.attributes.title) === packFilter.value.value)) {
        return false
    }

    return true
}))

const filteredEnglishGames = computed(() => data.value.englishGames.filter((englishGame) => {
    if (searchText.value) {
        if (!englishGame.title.toLowerCase().includes(searchText.value.toLowerCase())) {
            return false
        }
    }

    // a safe guard in case playerMin or playMax is missing
    if (!englishGame.features.counts) return false

    if (
        playerFilter?.value.value !== -1 && englishGame.features.counts
                    && (
                        playerFilter?.value.value < englishGame.features.counts.playerMin
                        || playerFilter?.value.value > englishGame.features.counts.playerMax
                    )
    ) {
        return false
    }

    if (packFilter.value.value === i18n.t('GAMES.ANY')) {
        return true
    }

    if (!((englishGame.packDetails?.data?.attributes.title) === packFilter.value.value)) {
        return false
    }

    return true
}))

const sortedGames = computed(() => {
    const games = filteredGames
    if (!games.value.length) {
        return null
    }

    switch (sortFilter.value.value) {
    case 'alphabetical':
        games.value.sort((a, b) => {
            const aTitle = sortableTitle(a.title)
            const bTitle = sortableTitle(b.title)
            return aTitle.localeCompare(bTitle)
        })
        break
        // case 'popularity':
        // TODO: Figure out how we quantify popularity,
        // then sort by it.
        // break
    case 'release-date':
        // releaseDate stored as ISO 8601, works with lexicographic sort
        // calling localeCompare on `b` produces descending sort
        games.value.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))
    }

    return games
})

const sortedEnglishGames = computed(() => {
    const englishGames = filteredEnglishGames.value

    if (!englishGames?.length) {
        return null
    }

    switch (sortFilter.value.value) {
    case 'alphabetical':
        englishGames.sort((a, b) => {
            const aTitle = sortableTitle(a.title)
            const bTitle = sortableTitle(b.title)
            return aTitle.localeCompare(bTitle)
        })
        break
        // case 'popularity':
        // TODO: Figure out how we quantify popularity,
        // then sort by it.
        // break
    case 'release-date':
        // releaseDate stored as ISO 8601, works with lexicographic sort
        // calling localeCompare on `b` produces descending sort
        englishGames.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))
    }
    return englishGames
})

const packOptions = computed(() => {
    const options = new Set<string>()

    if (data.value.games.length) {
        data.value.games.forEach(({ packDetails }) => {
            options.add(packDetails?.data?.attributes.title)
        })
    }

    if (data.value.englishGames.length) {
        data.value.englishGames.forEach(({ packDetails }) => {
            options.add(packDetails?.data?.attributes.title)
        })
    }

    return [
        { label: i18n.t('GAMES.ANY'), value: i18n.t('GAMES.ANY') },
        ...Array.from(options)
            .sort((a, b) => {
                if (a < b) {
                    return -1
                }
                if (a > b) {
                    return 1
                }
                return 0
            })
            .map((pack) => ({ label: pack, value: pack }))
    ]
})

const playerOptions = computed(() => {
    const options = new Set<number>()

    if (data.value.games.length) {
        data.value.games.forEach(({ features }) => {
            if (!features.counts) return
            if (!features.counts.playerMin || !features.counts.playerMax) return
            options.add(features.counts.playerMin)
            options.add(features.counts.playerMax)
        })
    }

    if (data.value.englishGames.length) {
        data.value.englishGames.forEach(({ features }) => {
            if (!features.counts) return
            if (!features.counts.playerMin || !features.counts.playerMax) return
            options.add(features.counts.playerMin)
            options.add(features.counts.playerMax)
        })
    }

    // We'll use a custom label for singleplayer games.
    // Deleting the value avoids showing it twice.
    options.delete(1)

    return [
        { label: i18n.t('GAMES.ANY'), value: -1 },
        ...Array.from(options)
            .sort((a, b) => b - a)
            .map((max) => ({ label: `${max} ${i18n.t('GAMES.PLAYERS')}`, value: max })),
        { label: i18n.t('GAMES.NO_FRIENDS'), value: 1 }
    ]
})

function sortableTitle(t:string) {
    let title: string = t.toLocaleLowerCase()
    if (title.startsWith('the')) {
        title = title.substring(3).trim()
    }
    return title
}

function onClear() {
    searchText.value = ''
    playerFilter.value = { label: i18n.t('GAMES.ANY'), value: -1 }
    packFilter.value = { label: i18n.t('GAMES.ANY'), value: i18n.t('GAMES.ANY') }
    sortFilter.value = sortOptions[0]
    // sortedGames.value = games.value
}

</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

.games {
    color: var(--neutral-100);
    // make footer appear sticky
    min-height: 100vh;

    .game-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        column-gap: 15px;
        row-gap: 12px;

        @include mq-medium-and-less {
            grid-template-columns: repeat(3, 1fr);
            column-gap: 12px;
            row-gap: 10px;
        }

        @include mq-small-and-less {
            grid-template-columns: repeat(2, 1fr);
            column-gap: 10px;
            row-gap: 8px;
        }
    }

    .grid-nav {
        display: flex;
        gap: 16px;
        margin: 32px 0;

        @include mq-small-and-less {
            flex-wrap: wrap;
        }

        .clear-view {
            color: var(--neutral-300);
            font-size: 14px;
            line-height: 14px;
            justify-content: center;
            max-height: 50px;
            width: 70%;

            @include mq-medium-and-more {
                font-size: 16px;
                line-height: 16px;
            }

            :deep(img) {
                height: 12px;
                width: 12px;
            }

            @include mq-small-and-less {
                width: calc(50% - 8px)
            }

            @include mq-xsmall {
                min-height: 34px;
                width: 100%;
            }
        }


        .filter {
            color: var(--neutral-300);
            font-family: Inter;
            font-size: 13px;
            max-height: 50px;
            width: 112%;

            @include mq-medium-and-more {
                font-size: 14px;
            }

            @include mq-small-and-less {
                width: calc(50% - 8px);
            }

            @include mq-xsmall {
                width: 100%;
            }

            :deep(.vs__selected) {
                // HACK: This prevents the prior selected value from jumping up
                // visually when the dropdown list is open.
                // TODO: Consider migrating to https://github.com/RemoteDevForce/tom-select
                flex-grow: 1;
                position: static;
                padding: 0;

                .selected {
                    flex-grow: 1;
                }
            }

            :deep(.vs__selected-options) {
                margin-left: 16px;

                @include mq-medium-and-less {
                    margin-left: 4px;
                }
            }

            :deep(.vs__open-indicator) {
                height: 16px;
                width: 16px;
                margin-right: 16px;

                @include mq-medium-and-less {
                    margin-right: 4px;
                }
            }

            :deep(.vs__dropdown-toggle) {
                max-height: 50px;
            }

            .selected {
                color: var(--neutral-300);
            }
        }

        .search {
            background-color: var(--surface-50);
            border: none;
            border-bottom: 2px solid var(--primary-300);
            border-radius: 4px 4px 0px 0px;
            margin-left: -32px;
            max-height: 50px;
            padding-left: 36px;
            width: 130%;
            z-index: 0;

            &::-webkit-input-placeholder {
                color: var(--neutral-300);
            }

            &:focus-visible {
                outline: 1px solid var(--primary-300);
            }

            @include mq-small-and-less {
                margin-left: -32px;
                min-height: 50px;
                width: calc(50% - 8px);
            }

            @include mq-xsmall {
                width: 100%;
            }
        }

        .search-icon {
            top: 14px;
            right: -12px;
            position: relative;
            z-index: 1;

            @include mq-medium-and-more {
                top: 14px;
            }

            @include mq-xsmall {
                top: 16px;
            }
        }
    }

    .locale-heading {
        font-size: 24px;
        margin: 56px 0px 24px 80px;
        text-transform: uppercase;

        @include mq-medium {
            font-size: 20px;
            margin-left: 40px;
        }

        @include mq-small {
            font-size: 20px;
            margin-left: 20px;
        }

        @include mq-xsmall {
            font-size: 18px;
            margin-left: 18px;
        }
    }

    :deep(.no-results .flex-row) {
        min-height: 200px;
    }
}
</style>
